import Page from 'attendee/theme/Page'
import React, {useEffect, useState} from 'react'
import styled from 'styled-components'
import {WhyOnlineEvent} from 'attendee/theme/WhyOnlineEvent'
import {Helmet} from 'react-helmet'
import {DEFAULT_OG_IMAGE, SITE_ORIGIN} from 'seo/siteMeta'

// Icons
import WebIcon from '@mui/icons-material/Web'
import PointOfSaleIcon from '@mui/icons-material/PointOfSale'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import VerifiedIcon from '@mui/icons-material/Verified'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import SyncAltIcon from '@mui/icons-material/SyncAlt'
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import LockPersonIcon from '@mui/icons-material/LockPerson'
import {routes} from 'routes/routes'
import {useI18n} from 'i18n/I18nContext'
import {withLocalePath} from 'i18n/localePaths'
import {fetchPublicEconomy} from 'platform/fetchPublicEconomy'

export function ForOrganizations() {
  const {locale, t} = useI18n()
  const pageUrl = `${SITE_ORIGIN}${withLocalePath(locale, routes.organizations)}`
  const helpUrl = withLocalePath(locale, routes.help)
  const orgLoginUrl = withLocalePath(locale, routes.organization.login)

  const [dayList, setDayList] = useState(14)
  const [dayPromo, setDayPromo] = useState<number | null>(null)
  const [dayDisplay, setDayDisplay] = useState(14)
  const [dayHasPromo, setDayHasPromo] = useState(false)

  useEffect(() => {
    fetchPublicEconomy()
      .then((e) => {
        setDayList(e.event_price_per_day)
        setDayPromo(e.promo_event_price_per_day)
        setDayDisplay(e.display_event_price_per_day)
        setDayHasPromo(Boolean(e.has_event_day_promo))
      })
      .catch(() => {})
  }, [])

  return (
    <Page>
      <>
        <Helmet>
          <title>{t('orgMar.metaTitle')}</title>
          <meta name="description" content={t('orgMar.metaDescription')} />
          <link rel="canonical" href={pageUrl} />
          <meta property="og:title" content={t('orgMar.ogTitle')} />
          <meta property="og:description" content={t('orgMar.ogDescription')} />
          <meta property="og:url" content={pageUrl} />
          <meta property="og:image" content={DEFAULT_OG_IMAGE} />
          <meta property="og:image:secure_url" content={DEFAULT_OG_IMAGE} />
          <meta property="og:image:type" content="image/jpeg" />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={t('orgMar.ogTitle')} />
          <meta
            name="twitter:description"
            content={t('orgMar.ogDescription')}
          />
          <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
        </Helmet>

        <section className="contact_section dark_light_class pt-55 pb-55">
          <div className="container">
            <div className="section__title-wrapper mb-80">
              <span className="section__back-title">
                {t('orgMar.heroBack')}
              </span>
              <span className="section__subtitle">
                {t('orgMar.pageSubtitle')}
              </span>
              <h1 className="section__title">
                {t('orgMar.pageTitleBefore')}{' '}
                <span className="text__highlight">
                  {t('orgMar.pageTitleHighlight')}
                </span>
              </h1>
            </div>

            <Hero>
              <HeroMain>
                <HeroBadge>{t('orgMar.heroBadge')}</HeroBadge>
                <HeroTitle>
                  {t('orgMar.heroSubtitleTitleBefore')}{' '}
                  <span className="text__highlight">
                    {t('orgMar.heroSubtitleTitleHighlight')}
                  </span>
                </HeroTitle>
                <HeroSubtitle>{t('orgMar.heroSubtitle')}</HeroSubtitle>

                <HeroCtas>
                  <PrimaryCta href={orgLoginUrl}>
                    {t('home.orgCtaRegister')}
                  </PrimaryCta>
                </HeroCtas>

                <HeroFine>{t('orgMar.heroFine')}</HeroFine>
              </HeroMain>

              <HeroSide>
                <KpiGrid>
                  <KpiCard>
                    <KpiValue>1</KpiValue>
                    <KpiLabel>{t('orgMar.kpi1')}</KpiLabel>
                  </KpiCard>
                  <KpiCard>
                    <KpiValue>100%</KpiValue>
                    <KpiLabel>{t('orgMar.kpi2')}</KpiLabel>
                  </KpiCard>
                  <KpiCard>
                    <KpiValue>{t('orgMar.kpi3Value')}</KpiValue>
                    <KpiLabel>{t('orgMar.kpi3')}</KpiLabel>
                  </KpiCard>
                  <KpiCard>
                    <KpiValue>0</KpiValue>
                    <KpiLabel>{t('orgMar.kpi4')}</KpiLabel>
                  </KpiCard>
                </KpiGrid>

                <SidePanel>
                  <SideTitle>{t('orgMar.sideTitle')}</SideTitle>
                  <SideText>
                    <b>{t('orgMar.sideBold')}</b>
                    {t('orgMar.sideAfterBold')}
                  </SideText>
                </SidePanel>
              </HeroSide>
            </Hero>

            <SectionHeader className="mt-90 mb-30">
              <span className="section__subtitle">
                {t('orgMar.compareSubtitle')}
              </span>
              <h2 className="section__title">
                {t('orgMar.compareTitleBefore')}{' '}
                <span className="text__highlight">
                  {t('orgMar.compareTitleHighlight')}
                </span>{' '}
                {t('orgMar.compareTitleAfter')}
              </h2>
            </SectionHeader>

            <CompareWrap>
              <CompareCol>
                <CompareHeader>{t('orgMar.compareOldHeader')}</CompareHeader>
                <CompareList>
                  <li>{t('orgMar.compareOld1')}</li>
                  <li>{t('orgMar.compareOld2')}</li>
                  <li>{t('orgMar.compareOld3')}</li>
                  <li>{t('orgMar.compareOld4')}</li>
                  <li>{t('orgMar.compareOld5')}</li>
                  <li>{t('orgMar.compareOld6')}</li>
                  <li>{t('orgMar.compareOld7')}</li>
                </CompareList>
              </CompareCol>

              <CompareCol>
                <CompareHeader>{t('orgMar.compareNewHeader')}</CompareHeader>
                <CompareList strong>
                  <li>{t('orgMar.compareNew1')}</li>
                  <li>{t('orgMar.compareNew2')}</li>
                  <li>{t('orgMar.compareNew3')}</li>
                  <li>{t('orgMar.compareNew4')}</li>
                  <li>{t('orgMar.compareNew5')}</li>
                  <li>{t('orgMar.compareNew6')}</li>
                  <li>{t('orgMar.compareNew7')}</li>
                </CompareList>
              </CompareCol>
            </CompareWrap>

            {/* PRIVATE PAGE - MAIN SELL */}
            <SectionHeader className="mt-90 mb-30">
              <span className="section__subtitle">
                {t('orgMar.privateSubtitle')}
              </span>
              <h2 className="section__title">
                {t('orgMar.privateTitleBefore')}{' '}
                <span className="text__highlight">
                  {t('orgMar.privateTitleHighlight')}
                </span>
              </h2>
            </SectionHeader>

            <TwoCol>
              <TextCard>
                <CardTitle>{t('orgMar.privateCard1Title')}</CardTitle>
                <CardText>{t('orgMar.privateCard1Text')}</CardText>
                <Bullets>
                  <li>{t('orgMar.privateBullet1')}</li>
                  <li>{t('orgMar.privateBullet2')}</li>
                  <li>{t('orgMar.privateBullet3')}</li>
                  <li>{t('orgMar.privateBullet4')}</li>
                  <li>{t('orgMar.privateBullet5')}</li>
                </Bullets>
              </TextCard>

              <TextCard>
                <CardTitle>{t('orgMar.privateCard2Title')}</CardTitle>
                <CardText>{t('orgMar.privateCard2Text')}</CardText>
                <InfoStrip>
                  <LockPersonIcon />
                  <div>{t('orgMar.privateStrip')}</div>
                </InfoStrip>
              </TextCard>
            </TwoCol>

            {/* BENEFITS */}
            <SectionHeader className="mt-80 mb-30">
              <span className="section__subtitle">
                {t('orgMar.benefitsSubtitle')}
              </span>
              <h2 className="section__title">
                {t('orgMar.benefitsTitleBefore')}{' '}
                <span className="text__highlight">
                  {t('orgMar.benefitsTitleHighlight')}
                </span>
                {t('orgMar.benefitsTitleAfter')}
              </h2>
            </SectionHeader>

            <TwoCol>
              <FeatureCard>
                <IconTop>
                  <PointOfSaleIcon />
                </IconTop>
                <CardTitle>{t('orgMar.featStripeTitle')}</CardTitle>
                <CardText>{t('orgMar.featStripeText')}</CardText>
              </FeatureCard>

              <FeatureCard>
                <IconTop>
                  <ReceiptLongIcon />
                </IconTop>
                <CardTitle>{t('orgMar.featInvoiceTitle')}</CardTitle>
                <CardText>{t('orgMar.featInvoiceText')}</CardText>
              </FeatureCard>

              <FeatureCard>
                <IconTop>
                  <NotificationsActiveIcon />
                </IconTop>
                <CardTitle>{t('orgMar.featRemindTitle')}</CardTitle>
                <CardText>{t('orgMar.featRemindText')}</CardText>
              </FeatureCard>

              <FeatureCard>
                <IconTop>
                  <AttachMoneyIcon />
                </IconTop>
                <CardTitle>{t('orgMar.featUpsellTitle')}</CardTitle>
                <CardText>{t('orgMar.featUpsellText')}</CardText>
              </FeatureCard>

              <FeatureCard>
                <IconTop>
                  <VerifiedIcon />
                </IconTop>
                <CardTitle>{t('orgMar.featCertTitle')}</CardTitle>
                <CardText>{t('orgMar.featCertText')}</CardText>
              </FeatureCard>

              <FeatureCard>
                <IconTop>
                  <SyncAltIcon />
                </IconTop>
                <CardTitle>{t('orgMar.featRefundTitle')}</CardTitle>
                <CardText>{t('orgMar.featRefundText')}</CardText>
              </FeatureCard>

              <FeatureCard>
                <IconTop>
                  <IntegrationInstructionsIcon />
                </IconTop>
                <CardTitle>{t('orgMar.featZapierTitle')}</CardTitle>
                <CardText>{t('orgMar.featZapierText')}</CardText>
              </FeatureCard>

              <FeatureCard>
                <IconTop>
                  <WebIcon />
                </IconTop>
                <CardTitle>{t('orgMar.featShowcaseTitle')}</CardTitle>
                <CardText>{t('orgMar.featShowcaseText')}</CardText>
              </FeatureCard>
            </TwoCol>

            {/* PRICING */}
            <SectionHeader className="mt-90 mb-30">
              <span className="section__subtitle">
                {t('orgMar.priceSubtitle')}
              </span>
              <h2 className="section__title">
                {t('orgMar.priceTitleBefore')}{' '}
                <span className="text__highlight">
                  {t('orgMar.priceTitleHighlight')}
                </span>
              </h2>
            </SectionHeader>

            <PricingHint>{t('orgMar.priceHint')}</PricingHint>

            <ThreeCol>
              <PriceCard>
                <PriceTop>
                  <PriceName>{t('orgMar.pricePaidName')}</PriceName>
                  <PriceValue>
                    {dayHasPromo && dayPromo != null ? (
                      <>
                        <s style={{opacity: 0.65, marginRight: 6}}>
                          {dayList.toFixed(2)} €
                        </s>
                        <span>
                          {dayPromo.toFixed(2)} €{' '}
                          <span>{t('orgMar.pricePerDaySuffix')}</span>
                        </span>
                      </>
                    ) : (
                      <>
                        {dayDisplay.toFixed(2)} €{' '}
                        <span>{t('orgMar.pricePerDaySuffix')}</span>
                      </>
                    )}
                  </PriceValue>
                </PriceTop>
                <PriceBullets>
                  <li>{t('orgMar.pricePaidB1')}</li>
                  <li>{t('orgMar.pricePaidB2')}</li>
                  <li>{t('orgMar.pricePaidB3')}</li>
                  <li>{t('orgMar.pricePaidB4')}</li>
                  <li>{t('orgMar.pricePaidB5')}</li>
                  <li>{t('orgMar.pricePaidB6')}</li>
                  <li>{t('orgMar.pricePaidB7')}</li>
                  <li>{t('orgMar.pricePaidB8')}</li>
                  <li>{t('orgMar.pricePaidB9')}</li>
                  <li>
                    <i>{t('orgMar.pricePaidNote')}</i>
                  </li>
                </PriceBullets>
                <PriceFooter>
                  <TagRow>
                    <Tag>
                      <LocalOfferIcon /> {t('orgMar.tagCoaching')}
                    </Tag>
                    <Tag>
                      <LocalOfferIcon /> {t('orgMar.tagTraining')}
                    </Tag>
                  </TagRow>
                  <PrimaryCta href={orgLoginUrl}>
                    {t('orgMar.startCta')}
                  </PrimaryCta>
                </PriceFooter>
              </PriceCard>

              <PriceCard>
                <PriceTop>
                  <PriceName>{t('orgMar.priceFreeName')}</PriceName>
                  <PriceValue>
                    {dayHasPromo && dayPromo != null ? (
                      <>
                        <s style={{opacity: 0.65, marginRight: 6}}>
                          {dayList.toFixed(2)} €
                        </s>
                        <span>
                          {dayPromo.toFixed(2)} €{' '}
                          <span>{t('orgMar.pricePerDaySuffix')}</span>
                        </span>
                      </>
                    ) : (
                      <>
                        {dayDisplay.toFixed(2)} €{' '}
                        <span>{t('orgMar.pricePerDaySuffix')}</span>
                      </>
                    )}
                  </PriceValue>
                </PriceTop>
                <PriceBullets>
                  <li>{t('orgMar.priceFreeB1')}</li>
                  <li>{t('orgMar.priceFreeB2')}</li>
                  <li>{t('orgMar.priceFreeB3')}</li>
                  <li>{t('orgMar.priceFreeB4')}</li>
                  <li>{t('orgMar.priceFreeB5')}</li>
                  <li>{t('orgMar.priceFreeB6')}</li>
                  <li>{t('orgMar.priceFreeB7')}</li>
                  <li>{t('orgMar.priceFreeB8')}</li>
                  <li>{t('orgMar.priceFreeB9')}</li>
                  <li>
                    <i>{t('orgMar.priceFreeNote')}</i>
                  </li>
                </PriceBullets>
                <PriceFooter>
                  <TagRow>
                    <Tag>
                      <LocalOfferIcon /> {t('orgMar.tagPremiere')}
                    </Tag>
                    <Tag>
                      <LocalOfferIcon /> {t('orgMar.tagPromo')}
                    </Tag>
                  </TagRow>
                  <PrimaryCta href={orgLoginUrl}>
                    {t('orgMar.startCta')}
                  </PrimaryCta>
                </PriceFooter>
              </PriceCard>

              <PriceCard>
                <PriceTop>
                  <PriceName>{t('orgMar.priceCharityName')}</PriceName>
                  <PriceValue>{t('orgMar.priceCharityValue')}</PriceValue>
                </PriceTop>
                <PriceBullets>
                  <li>{t('orgMar.priceCharityB1')}</li>
                  <li>{t('orgMar.priceCharityB2')}</li>
                  <li>{t('orgMar.priceCharityB3')}</li>
                  <li>{t('orgMar.priceCharityB4')}</li>
                  <li>{t('orgMar.priceCharityB5')}</li>
                  <li>{t('orgMar.priceCharityB6')}</li>
                  <li>{t('orgMar.priceCharityB7')}</li>
                  <li>
                    <i>{t('orgMar.priceCharityNote')}</i>
                  </li>
                </PriceBullets>
                <PriceFooter>
                  <CardText style={{marginBottom: 12}}>
                    {t('orgMar.priceCharityFooter')}
                  </CardText>
                  <SecondaryCta href={helpUrl}>
                    {t('orgMar.contactCta')}
                  </SecondaryCta>
                </PriceFooter>
              </PriceCard>
            </ThreeCol>

            <OneCol>
              <AddonCard>
                <AddonTitle>{t('orgMar.addonTitle')}</AddonTitle>
                <AddonList>
                  <li>
                    <b>19 €</b> {t('orgMar.addonSmallSuffix')}
                  </li>
                  <li>
                    <b>49 €</b> {t('orgMar.addonMidSuffix')}
                  </li>
                  <li>
                    <b>139 €</b> {t('orgMar.addonLargeSuffix')}
                  </li>
                </AddonList>
                <AddonNote>{t('orgMar.addonNote')}</AddonNote>
              </AddonCard>
            </OneCol>

            {/* STEPS */}
            <SectionHeader className="mt-90 mb-30">
              <span className="section__subtitle">
                {t('orgMar.stepsSubtitle')}
              </span>
              <h2 className="section__title">
                {t('orgMar.stepsTitleBefore')}{' '}
                <span className="text__highlight">
                  {t('orgMar.stepsTitleHighlight')}
                </span>
              </h2>
            </SectionHeader>

            <Steps>
              <Step>
                <StepIndex>1</StepIndex>
                <StepTitle>{t('orgMar.step1Title')}</StepTitle>
                <StepText>{t('orgMar.step1Text')}</StepText>
              </Step>

              <Step>
                <StepIndex>2</StepIndex>
                <StepTitle>{t('orgMar.step2Title')}</StepTitle>
                <StepText>{t('orgMar.step2Text')}</StepText>
              </Step>

              <Step>
                <StepIndex>3</StepIndex>
                <StepTitle>{t('orgMar.step3Title')}</StepTitle>
                <StepText>{t('orgMar.step3Text')}</StepText>
              </Step>

              <Step>
                <StepIndex>4</StepIndex>
                <StepTitle>{t('orgMar.step4Title')}</StepTitle>
                <StepText>{t('orgMar.step4Text')}</StepText>
              </Step>
            </Steps>

            {/* FINAL CTA */}
            <CtaBox className="mt-90">
              <CtaTitle>{t('orgMar.finalTitle')}</CtaTitle>
              <CtaText>{t('orgMar.finalText')}</CtaText>
              <CtaRow>
                <PrimaryCta href={orgLoginUrl}>
                  {t('orgMar.finalCtaPrimary')}
                </PrimaryCta>
                <SecondaryCta href={helpUrl}>
                  {t('orgMar.finalCtaSecondary')}
                </SecondaryCta>
              </CtaRow>
            </CtaBox>
          </div>
        </section>

        <WhyOnlineEvent />
      </>
    </Page>
  )
}

/* ----------------------- styles ----------------------- */
// Styles unchanged from the original layout.

const SectionHeader = styled.div``

const Hero = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;

  @media (min-width: 992px) {
    grid-template-columns: 1.2fr 0.8fr;
    gap: 18px;
    align-items: start;
  }
`

const HeroMain = styled.div`
  background: #f5f5f5;
  border-radius: 10px;
  padding: 22px;
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.05);
`

const HeroSide = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const HeroBadge = styled.div`
  display: inline-flex;
  width: fit-content;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.06);
  font-weight: 700;
  margin-bottom: 10px;
`

const HeroTitle = styled.h2`
  margin: 0 0 10px 0;
  font-weight: 900;
  line-height: 1.15;
`

const HeroSubtitle = styled.p`
  margin: 0 0 12px 0;
  opacity: 0.9;
`

const HeroFine = styled.div`
  margin-top: 14px;
  font-size: 0.95rem;
  opacity: 0.85;
`

const HeroCtas = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 14px;
`

const PrimaryCta = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 14px;
  border-radius: 8px;
  text-decoration: none;
  color: #fff;
  font-weight: 900;
  background: var(--clr-gradient-14);
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0px 6px 18px rgba(0, 0, 0, 0.12);
  }
`

const SecondaryCta = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 14px;
  border-radius: 8px;
  text-decoration: none;
  color: var(--clr-common-black);
  font-weight: 900;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.12);

  &:hover {
    border-color: rgba(0, 0, 0, 0.22);
  }
`

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`

const KpiCard = styled.div`
  background: #f5f5f5;
  border-radius: 10px;
  padding: 14px;
`

const KpiValue = styled.div`
  font-size: 1.3rem;
  font-weight: 950;
`

const KpiLabel = styled.div`
  font-size: 0.9rem;
  opacity: 0.85;
`

const SidePanel = styled.div`
  background: #ffffff;
  border-radius: 10px;
  padding: 16px;
  border: 1px solid rgba(0, 0, 0, 0.08);
`

const SideTitle = styled.div`
  font-weight: 950;
  margin-bottom: 8px;
`

const SideText = styled.div`
  opacity: 0.92;
  line-height: 1.45;
`

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;

  @media (min-width: 992px) {
    grid-template-columns: 1fr 1fr;
    gap: 14px;
  }
`

const OneCol = styled.div`
  display: grid;
  grid-template-columns: 1fr;
`

const ThreeCol = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;

  @media (min-width: 992px) {
    grid-template-columns: 1fr 1fr 1fr;
    gap: 14px;
  }
`

const TextCard = styled.div`
  background: #ffffff;
  border-radius: 10px;
  padding: 18px;
  border: 1px solid rgba(0, 0, 0, 0.08);
`

const FeatureCard = styled.div`
  background: #f5f5f5;
  border-radius: 10px;
  padding: 18px;
  border: 1px solid rgba(0, 0, 0, 0.06);
`

const IconTop = styled.div`
  width: 54px;
  height: 54px;
  border-radius: 12px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;

  & > svg {
    font-size: 2rem;
    color: var(--clr-common-black);
  }
`

const CardTitle = styled.h4`
  margin: 0 0 8px 0;
  font-weight: 950;
  font-size: 1.05rem;
  line-height: 1.25;
`

const CardText = styled.p`
  margin: 0;
  opacity: 0.92;
  line-height: 1.5;
`

const Bullets = styled.ul`
  margin: 12px 0 0 0;
  padding-left: 18px;

  & > li {
    margin: 7px 0;
  }
`

const InfoStrip = styled.div`
  margin-top: 14px;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 10px;
  padding: 12px;
  display: grid;
  grid-template-columns: 30px 1fr;
  gap: 10px;
  align-items: start;

  & > svg {
    margin-top: 2px;
    opacity: 0.85;
  }
`

const CompareWrap = styled.div`
  background: #f5f5f5;
  border-radius: 10px;
  padding: 16px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;

  @media (min-width: 992px) {
    grid-template-columns: 1fr 1fr;
  }
`

const CompareCol = styled.div`
  background: #fff;
  border-radius: 10px;
  padding: 16px;
`

const CompareHeader = styled.div`
  font-weight: 950;
  margin-bottom: 10px;
`

const CompareList = styled.ul<{strong?: boolean}>`
  margin: 0;
  padding-left: 18px;

  & > li {
    margin: 7px 0;
    font-weight: ${(p) => (p.strong ? 650 : 400)};
  }
`

const PricingHint = styled.div`
  margin-top: -10px;
  margin-bottom: 10px;
  opacity: 0.85;
`

const PriceCard = styled.div`
  background: #ffffff;
  border-radius: 10px;
  padding: 18px;
  border: 1px solid rgba(0, 0, 0, 0.08);
`

const PriceTop = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  margin-bottom: 12px;
`

const PriceName = styled.div`
  font-weight: 950;
  font-size: 1.05rem;
`

const PriceValue = styled.div`
  font-weight: 950;
  font-size: 1.4rem;

  & > span {
    font-weight: 700;
    font-size: 0.95rem;
    opacity: 0.8;
  }
`

const PriceBullets = styled.ul`
  margin: 0;
  padding-left: 18px;

  & > li {
    margin: 7px 0;
  }
`

const PriceFooter = styled.div`
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const Tag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.05);
  font-weight: 700;
  font-size: 0.9rem;

  & > svg {
    font-size: 1.05rem;
    opacity: 0.85;
  }
`

const AddonCard = styled.div`
  background: #f5f5f5;
  border-radius: 10px;
  padding: 18px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  margin-top: 14px;
`

const AddonTitle = styled.h4`
  margin: 0 0 10px 0;
  font-weight: 950;
`

const AddonList = styled.ul`
  margin: 0;
  padding-left: 18px;

  & > li {
    margin: 8px 0;
  }
`

const AddonNote = styled.div`
  margin: 10px 0 14px 0;
  opacity: 0.85;
`

const Steps = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;

  @media (min-width: 992px) {
    grid-template-columns: 1fr 1fr;
  }
`

const Step = styled.div`
  background: #f5f5f5;
  border-radius: 10px;
  padding: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
`

const StepIndex = styled.div`
  width: 34px;
  height: 34px;
  line-height: 34px;
  text-align: center;
  border-radius: 50%;
  background: var(--clr-gradient-14);
  color: #fff;
  font-weight: 950;
  margin-bottom: 10px;
`

const StepTitle = styled.div`
  font-weight: 950;
  margin-bottom: 6px;
`

const StepText = styled.div`
  opacity: 0.92;
`

const CtaBox = styled.div`
  border-radius: 12px;
  padding: 22px;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.04), rgba(0, 0, 0, 0.02));
  border: 1px solid rgba(0, 0, 0, 0.08);
`

const CtaTitle = styled.h3`
  margin: 0 0 8px 0;
  font-weight: 950;
`

const CtaText = styled.p`
  margin: 0 0 14px 0;
  opacity: 0.9;
`

const CtaRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
`
